import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { authResetPassword, formatApiErrorDetail } from "../lib/api";
import { Reveal } from "../components/Motion";

export default function ResetPassword() {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Set New Password — Motion & Meaning";
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await authResetPassword({ token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center" data-testid="reset-password-page">
      <div className="mm-vignette" />
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 w-full pt-32 pb-24">
        <div className="max-w-md">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.4em] text-gold mb-6">ACCOUNT RECOVERY</p>
            {!token ? (
              <div data-testid="reset-invalid">
                <h1 className="font-serif text-5xl md:text-6xl font-light leading-[0.95] text-white">
                  Invalid link.
                </h1>
                <p className="text-white/60 leading-relaxed mt-8">
                  This reset link is missing or malformed. Request a new one below.
                </p>
                <Link to="/forgot-password" data-testid="reset-request-new" className="inline-block mt-10 font-mono text-[11px] tracking-[0.25em] text-gold hover:text-white transition-colors">
                  REQUEST A NEW LINK →
                </Link>
              </div>
            ) : done ? (
              <div data-testid="reset-success">
                <h1 className="font-serif text-5xl md:text-6xl font-light leading-[0.95] text-white">
                  Password<br />restored.
                </h1>
                <p className="text-white/60 leading-relaxed mt-8">
                  Your new password is active. Sign in to return to your account.
                </p>
                <Link
                  to="/login"
                  data-testid="reset-go-login"
                  className="inline-block mt-10 bg-gold text-black font-mono text-[11px] tracking-[0.25em] px-10 py-4 hover:bg-white transition-colors"
                >
                  SIGN IN
                </Link>
              </div>
            ) : (
              <>
                <h1 className="font-serif text-5xl md:text-6xl font-light leading-[0.95] text-white">
                  Set a new<br />password.
                </h1>
                <form onSubmit={submit} className="mt-12 space-y-8" data-testid="reset-form">
                  <div>
                    <label htmlFor="reset-password-input" className="font-mono text-[10px] tracking-[0.25em] text-white/40 block mb-3">NEW PASSWORD</label>
                    <input
                      id="reset-password-input"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="reset-password"
                      className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg text-white transition-colors"
                      placeholder="••••••••"
                    />
                  </div>
                  <div>
                    <label htmlFor="reset-confirm-input" className="font-mono text-[10px] tracking-[0.25em] text-white/40 block mb-3">CONFIRM PASSWORD</label>
                    <input
                      id="reset-confirm-input"
                      type="password"
                      required
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      data-testid="reset-confirm"
                      className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg text-white transition-colors"
                      placeholder="••••••••"
                    />
                  </div>
                  {error && (
                    <div data-testid="reset-error">
                      <p className="text-red-500 text-sm font-mono">{error}</p>
                      {error.toLowerCase().includes("expired") && (
                        <Link to="/forgot-password" data-testid="reset-request-new" className="inline-block mt-3 font-mono text-[10px] tracking-[0.25em] text-gold hover:text-white transition-colors">
                          REQUEST A NEW LINK →
                        </Link>
                      )}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="reset-submit"
                    className="w-full bg-gold text-black font-mono text-[11px] tracking-[0.25em] py-4 hover:bg-white transition-colors disabled:opacity-50"
                  >
                    {loading ? "RESTORING..." : "RESET PASSWORD"}
                  </button>
                </form>
              </>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
